/* @jsxImportSource @emotion/react */

import React, { useState } from 'react'
import { css } from '@emotion/react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Checkbox, Input, isValidEmail, Snackbar } from '@slite/components'
import { ErrorIcon } from '@slite/icons'

import { useRoutes } from '../../routes'
import type { LeadSignupSource } from '../../server/leadSignup'
import { readAttributionParams } from '../../utils/attribution'
import { ButtonSubmit } from '../ButtonSubmit'
import { FormField } from '../FormField'
import { readUnlockToken, rememberLeadGateSignup } from './leadGateAccess'

/* The email and company-website fields are too wide to share a row on a
   phone; the shorter name pair stays side by side. */
const wideFieldRowCss = css`
  display: flex;
  gap: 12px;

  @media screen and (max-width: 40rem) {
    flex-direction: column;
    gap: 16px;
  }
`

const decoyFieldStyle: React.CSSProperties = {
  position: 'absolute',
  left: -10000,
  width: 1,
  height: 1,
  overflow: 'hidden',
}

/* Records the lead only; the caller decides what unlocking means. The message
   ids keep the ebook prefix: renaming them would blank the translations. */
export const LeadCaptureForm = ({
  source,
  contentSlug,
  submitLabel,
  onSuccess,
  firstNameInputRef,
}: {
  source: LeadSignupSource
  contentSlug?: string
  submitLabel: string
  onSuccess: () => void
  firstNameInputRef?: React.Ref<HTMLInputElement>
}) => {
  const intl = useIntl()
  const routes = useRoutes()
  const [formContent, setFormContent] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyWebsite: '',
    hasAgreedToPrivacyPolicy: false,
    sliteNlOptIn: false,
    referral: '',
  })
  const [errorMessage, setErrorMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setErrorMessage('')

    if (!isValidEmail(formContent.email)) {
      setErrorMessage(
        intl.formatMessage({
          id: 'website.ebookCompanyBrain.form.errors.email',
          defaultMessage: 'Please enter a valid email',
        })
      )
      return
    }

    setIsSubmitting(true)

    let unlockToken: string | null
    try {
      const response = await fetch('/website/api/lead-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source,
          contentSlug,
          firstName: formContent.firstName,
          lastName: formContent.lastName,
          email: formContent.email,
          companyWebsite: formContent.companyWebsite,
          sliteNlOptIn: formContent.sliteNlOptIn,
          referral: formContent.referral,
          utmCampaign: readAttributionParams().utm_campaign,
        }),
      })
      if (!response.ok) throw new Error(`Signup failed: ${response.status}`)
      unlockToken = await readUnlockToken(response)
    } catch {
      setIsSubmitting(false)
      setErrorMessage(
        intl.formatMessage({
          id: 'website.ebookCompanyBrain.form.errors.submit',
          defaultMessage: 'Something went wrong, please try again.',
        })
      )
      return
    }

    rememberLeadGateSignup(
      unlockToken,
      contentSlug ? { source, contentSlug } : null
    )
    onSuccess()
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flexbox flexbox-column flexbox-gap-16 width-full"
    >
      {errorMessage && (
        <Snackbar icon={ErrorIcon} radiusSize="large" variant="danger">
          {errorMessage}
        </Snackbar>
      )}
      <div className="flexbox flexbox-gap-12">
        <FormField
          label={intl.formatMessage({
            id: 'website.ebookCompanyBrain.form.firstNameLabel',
            defaultMessage: 'First name',
          })}
        >
          <Input
            ref={firstNameInputRef}
            type="text"
            name="firstName"
            wrapperClassName="background-shade-100"
            size="large"
            placeholder={intl.formatMessage({
              id: 'website.ebookCompanyBrain.form.firstNamePlaceholder',
              defaultMessage: 'Roger',
            })}
            value={formContent.firstName}
            onChange={(e) =>
              setFormContent({ ...formContent, firstName: e.target.value })
            }
            required={true}
          />
        </FormField>
        <FormField
          label={intl.formatMessage({
            id: 'website.ebookCompanyBrain.form.lastNameLabel',
            defaultMessage: 'Last name',
          })}
        >
          <Input
            type="text"
            name="lastName"
            wrapperClassName="background-shade-100"
            size="large"
            placeholder={intl.formatMessage({
              id: 'website.ebookCompanyBrain.form.lastNamePlaceholder',
              defaultMessage: 'Smith',
            })}
            value={formContent.lastName}
            onChange={(e) =>
              setFormContent({ ...formContent, lastName: e.target.value })
            }
            required={true}
          />
        </FormField>
      </div>
      <div css={wideFieldRowCss}>
        <FormField
          label={intl.formatMessage({
            id: 'website.ebookCompanyBrain.form.emailLabel',
            defaultMessage: 'Work email',
          })}
        >
          <Input
            type="email"
            name="email"
            wrapperClassName="background-shade-100"
            size="large"
            placeholder={intl.formatMessage({
              id: 'website.ebookCompanyBrain.form.emailPlaceholder',
              defaultMessage: 'roger@acme.inc',
            })}
            value={formContent.email}
            onChange={(e) =>
              setFormContent({ ...formContent, email: e.target.value })
            }
            required={true}
          />
        </FormField>
        <FormField
          label={intl.formatMessage({
            id: 'website.ebookCompanyBrain.form.companyWebsiteLabel',
            defaultMessage: 'Company website',
          })}
        >
          <Input
            type="text"
            name="companyWebsite"
            wrapperClassName="background-shade-100"
            size="large"
            autoComplete="url"
            placeholder={intl.formatMessage({
              id: 'website.ebookCompanyBrain.form.companyWebsitePlaceholder',
              defaultMessage: 'acme.inc',
            })}
            value={formContent.companyWebsite}
            onChange={(e) =>
              setFormContent({ ...formContent, companyWebsite: e.target.value })
            }
            required={true}
          />
        </FormField>
      </div>
      <div className="flexbox flexbox-column flexbox-gap-12 margin-top-8">
        <label className="flexbox flexbox-align-start flexbox-gap-8 pointer">
          <Checkbox
            className="flexbox-no-shrink"
            style={{ marginTop: 2 }}
            name="privacyPolicy"
            checked={formContent.hasAgreedToPrivacyPolicy}
            onChange={(e) =>
              setFormContent({
                ...formContent,
                hasAgreedToPrivacyPolicy: e.target.checked,
              })
            }
            required={true}
          />
          <span className="font-size-7 shade-10">
            <FormattedMessage
              id="website.ebookCompanyBrain.form.privacyConsent"
              defaultMessage="I agree to the <link>Privacy Policy</link> and to sharing my information with Slite"
              values={{
                link: (text: React.ReactNode) => (
                  <a
                    href={routes.legal.privacy()}
                    target="_blank"
                    rel="noreferrer"
                    className="underline shade-10"
                  >
                    {text}
                  </a>
                ),
              }}
            />
          </span>
        </label>
        <label className="flexbox flexbox-align-start flexbox-gap-8 pointer">
          <Checkbox
            className="flexbox-no-shrink"
            style={{ marginTop: 2 }}
            name="wantsUpdates"
            checked={formContent.sliteNlOptIn}
            onChange={(e) =>
              setFormContent({ ...formContent, sliteNlOptIn: e.target.checked })
            }
          />
          <span className="font-size-7 shade-10">
            <FormattedMessage
              id="website.ebookCompanyBrain.form.updatesOptIn"
              defaultMessage="Keep me posted with Slite updates and new resources."
            />
          </span>
        </label>
      </div>
      <div aria-hidden="true" style={decoyFieldStyle}>
        <input
          type="text"
          name="referral"
          tabIndex={-1}
          autoComplete="off"
          value={formContent.referral}
          onChange={(e) =>
            setFormContent({ ...formContent, referral: e.target.value })
          }
        />
      </div>
      <ButtonSubmit
        disabled={isSubmitting}
        variant="primary"
        size="huge"
        className="width-full margin-top-16"
      >
        {submitLabel}
      </ButtonSubmit>
    </form>
  )
}
